import { useState, useCallback, useRef, useEffect } from 'react'
import { useNewsEvents } from '@/hooks/useNewsEvents'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Upload, FileJson, CheckCircle, XCircle, AlertCircle, Loader2, ClipboardPaste } from 'lucide-react'
import { toast } from 'sonner'

export function ImportManager() {
  const { importJson, isImporting } = useNewsEvents()
  const [isDragging, setIsDragging] = useState(false)
  const [importResult, setImportResult] = useState<any>(null)
  const [jsonText, setJsonText] = useState('')
  const [isImportingText, setIsImportingText] = useState(false)
  const resultRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (importResult && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [importResult])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback(() => {
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)

    const files = Array.from(e.dataTransfer.files)
    const jsonFile = files.find(f => f.type === 'application/json' || f.name.endsWith('.json'))

    if (jsonFile) {
      await handleFileUpload(jsonFile)
    }
  }, [])

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      await handleFileUpload(file)
    }
  }, [])

  const handleFileUpload = async (file: File) => {
    try {
      setImportResult(null)
      const result = await importJson(file)
      setImportResult(result)
    } catch (error: any) {
      setImportResult({
        success: false,
        message: error.message || 'Import failed',
        imported: 0,
        updated: 0,
        skipped: 0
      })
    }
  }

  const handleJsonTextImport = async () => {
    if (!jsonText.trim()) {
      toast.error('Inserisci il JSON prima di importare')
      return
    }
    
    try {
      setIsImportingText(true)
      setImportResult(null)
      toast.loading('Importazione in corso...', { id: 'import-json' })
      
      JSON.parse(jsonText)
      
      const blob = new Blob([jsonText], { type: 'application/json' })
      const file = new File([blob], 'import.json', { type: 'application/json' })
      
      const result = await importJson(file)
      setImportResult(result)
      
      if (result.success) {
        toast.success(`Importazione completata! ${result.imported} nuovi, ${result.updated} aggiornati`, { id: 'import-json' })
        setJsonText('')
      } else {
        toast.error(result.message || 'Importazione fallita', { id: 'import-json' })
      }
    } catch (error: any) {
      if (error instanceof SyntaxError) {
        const errorResult = {
          success: false,
          message: 'JSON non valido: ' + error.message,
          imported: 0,
          updated: 0,
          skipped: 0
        }
        setImportResult(errorResult)
        toast.error('JSON non valido - controlla il formato', { id: 'import-json' })
      } else {
        const errorResult = {
          success: false,
          message: error.message || 'Import failed',
          imported: 0,
          updated: 0,
          skipped: 0
        }
        setImportResult(errorResult)
        toast.error(error.message || 'Importazione fallita', { id: 'import-json' })
      }
    } finally {
      setIsImportingText(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">JSON Import Manager</h1>
        <p className="text-muted-foreground">
          Import Forex Factory news events from JSON files
        </p>
      </div>

      {/* Import Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>📋 Import Instructions</CardTitle>
          <CardDescription>
            Follow these steps to import news events successfully
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <h3 className="font-medium">✅ Supported Format</h3>
            <p className="text-sm text-muted-foreground">
              JSON file with array of news events from Forex Factory
            </p>
            <div className="rounded-lg bg-muted p-4">
              <code className="text-sm">
                {`[
  {
    "title": "Non-Farm Payrolls",
    "country": "USD",
    "date": "Jan 05, 2024",
    "time": "13:30",
    "impact": "High",
    "forecast": "180K",
    "previous": "199K"
  }
]`}
              </code>
            </div>
          </div>

          <div className="space-y-2">
            <h3 className="font-medium">🎯 Filtered Currencies</h3>
            <div className="flex gap-2">
              <Badge>USD</Badge>
              <Badge>AUD</Badge>
              <Badge>CAD</Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              Only events for these currencies will be imported
            </p>
          </div>

          <div className="space-y-2">
            <h3 className="font-medium">🔄 Smart Deduplication</h3>
            <p className="text-sm text-muted-foreground">
              System automatically:
            </p>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
              <li>Skips events manually edited by admins</li>
              <li>Updates existing events with new data</li>
              <li>Imports only new events</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Paste JSON Text */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardPaste className="h-5 w-5" />
            Incolla JSON
          </CardTitle>
          <CardDescription>
            Incolla direttamente il calendario settimanale in formato JSON
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            placeholder={`[
  {
    "title": "Non-Farm Payrolls",
    "country": "USD",
    "date": "Jan 05, 2024",
    "time": "13:30",
    "impact": "High",
    "forecast": "180K",
    "previous": "199K"
  }
]`}
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            className="min-h-[200px] font-mono text-sm"
            disabled={isImportingText}
          />
          <Button 
            onClick={handleJsonTextImport}
            disabled={!jsonText.trim() || isImportingText}
            className="w-full"
          >
            {isImportingText ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Importazione in corso...
              </>
            ) : (
              <>
                <ClipboardPaste className="mr-2 h-4 w-4" />
                Importa JSON
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Drag & Drop Zone */}
      <Card>
        <CardHeader>
          <CardTitle>Upload JSON File</CardTitle>
          <CardDescription>
            Drag and drop or click to browse
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`
              relative rounded-lg border-2 border-dashed p-12 text-center transition-colors
              ${isDragging ? 'border-primary bg-primary/10' : 'border-muted-foreground/25'}
              ${isImporting ? 'pointer-events-none opacity-50' : 'cursor-pointer hover:border-primary'}
            `}
          >
            <input
              type="file"
              accept=".json,application/json"
              onChange={handleFileSelect}
              className="absolute inset-0 cursor-pointer opacity-0"
              disabled={isImporting}
            />
            
            <div className="flex flex-col items-center gap-4">
              {isImporting ? (
                <>
                  <Loader2 className="h-12 w-12 animate-spin text-primary" />
                  <p className="text-lg font-medium">Importazione in corso...</p>
                  <p className="text-sm text-muted-foreground">
                    Attendere prego, stiamo processando il file
                  </p>
                </>
              ) : (
                <>
                  <FileJson className="h-12 w-12 text-muted-foreground" />
                  <div>
                    <p className="text-lg font-medium">Trascina il file JSON qui</p>
                    <p className="text-sm text-muted-foreground">
                      oppure clicca per selezionare
                    </p>
                  </div>
                  <Button variant="outline" disabled={isImporting}>
                    <Upload className="mr-2 h-4 w-4" />
                    Seleziona File
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Import Results */}
      {importResult && (
        <Card ref={resultRef} className={importResult.success ? "border-emerald-500/50 bg-emerald-500/5" : "border-red-500/50 bg-red-500/5"}>
          <CardHeader>
            <div className="flex items-center gap-2">
              {importResult.success ? (
                <CheckCircle className="h-6 w-6 text-emerald-500" />
              ) : (
                <XCircle className="h-6 w-6 text-red-500" />
              )}
              <CardTitle className="text-xl">
                {importResult.success ? '✅ Importazione Completata!' : '❌ Importazione Fallita'}
              </CardTitle>
            </div>
            <CardDescription className="text-base mt-2">{importResult.message}</CardDescription>
          </CardHeader>
          {importResult.success && (
            <CardContent>
              <div className="grid gap-4 md:grid-cols-4">
                <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-emerald-500" />
                    <span className="text-sm font-medium">Nuovi</span>
                  </div>
                  <p className="mt-2 text-3xl font-bold text-emerald-500">{importResult.imported}</p>
                </div>
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-amber-500" />
                    <span className="text-sm font-medium">Aggiornati</span>
                  </div>
                  <p className="mt-2 text-3xl font-bold text-amber-500">{importResult.updated}</p>
                </div>
                <div className="rounded-lg border border-gray-500/30 bg-gray-500/10 p-4">
                  <div className="flex items-center gap-2">
                    <XCircle className="h-5 w-5 text-gray-500" />
                    <span className="text-sm font-medium">Saltati</span>
                  </div>
                  <p className="mt-2 text-3xl font-bold text-gray-500">{importResult.skipped}</p>
                </div>
                <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-4">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-blue-500" />
                    <span className="text-sm font-medium">Filtrati</span>
                  </div>
                  <p className="mt-2 text-3xl font-bold text-blue-500">{importResult.filtered || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">Altre valute</p>
                </div>
              </div>
            </CardContent>
          )}
        </Card>
      )}
    </div>
  )
}
